import React, { useState } from "react";
import {
    Box,
    Heading,
    Text,
    Input,
    Textarea,
    Button,
    VStack,
    FormControl,
    FormLabel,
    Container,
    useToast,
    Flex,
    Icon,
    HStack,
    useDisclosure,
    SimpleGrid
} from "@chakra-ui/react";
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaClock, FaShieldAlt, FaPaperPlane, FaUserTie } from "react-icons/fa";
import MapModal from "../../components/modals/MapModal";
import SEO from '../../components/SEO';
import { useLanguage } from "../../context/LanguageContext";

const ConnectPage = () => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        name: "", lastname: "", surname: "", email: "", phone: "", message: ""
    });

    const toast = useToast();
    const { isOpen: isMapOpen, onOpen: onMapOpen, onClose: onMapClose } = useDisclosure();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_ENDPOINT + "/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            if (!response.ok) throw new Error("Submission failed");
            toast({
                title: t("connect.toastSuccessTitle"),
                description: t("connect.toastSuccessDesc"),
                status: "success",
                duration: 5000,
                isClosable: true,
                position: "top"
            });
            setFormData({ name: "", lastname: "", surname: "", email: "", phone: "", message: "" });
        } catch (error) {
            toast({
                title: t("connect.toastErrorTitle"),
                description: t("connect.toastErrorDesc"),
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "name": "LegalTitan Կապ",
        "url": "https://legaltitan.am/contact",
        "description": "Կապ հաստատեք LegalTitan իրավաբանական թիմի հետ՝ ձեր իրավունքի պաշտպանության համար։",
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+37477531309",
            "contactType": "customer service",
            "areaServed": "AM",
            "availableLanguage": ["Armenian", "English", "Russian"]
        }
    };

    return (
        <Box bg="#f8fafc" minH="100vh">
            <SEO
                title="Կապ Մեզ Հետ | LegalTitan"
                description="Կապ հաստատեք LegalTitan-ի հետ իրավաբանական ծառայությունների վերաբերյալ հարցումների և աջակցման համար։"
                keywords="իրավաբանական ծառայություններ, կապ, հարցում, աջակցություն, փաստաբաններ"
                url="https://legaltitan.am/contact"
                image="https://legaltitan.am/assets/images/legalLogo.jpg"
                fbAppId="61557661694303"
                structuredData={structuredData}
            />

            {/* Executive Hero Header */}
            <Box
                bg="#070d1e"
                color="white"
                pt={{ base: 14, md: 20 }}
                pb={{ base: 16, md: 24 }}
                position="relative"
                overflow="hidden"
            >
                <Box
                    position="absolute"
                    top="-30%"
                    left="30%"
                    w="500px"
                    h="500px"
                    bg="radial-gradient(circle, rgba(29, 78, 216, 0.2) 0%, transparent 70%)"
                    pointerEvents="none"
                />

                <Container maxW="container.xl" px={{ base: 4, md: 8 }} position="relative" zIndex={1} textAlign="center">
                    <Heading
                        as="h1"
                        fontSize={{ base: "30px", sm: "40px", md: "52px" }}
                        fontWeight="800"
                        letterSpacing="-0.02em"
                        mb={5}
                    >
                        {t("connect.heroTitle")}
                    </Heading>

                    <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color="gray.300"
                        maxW="720px"
                        mx="auto"
                        lineHeight="1.8"
                        mb={6}
                    >
                        {t("connect.heroText")}
                    </Text>

                    <HStack spacing={6} justify="center" mb={6} flexWrap="wrap">
                        <HStack spacing={2}><Icon as={FaUserTie} color="#dfba73" /><Text fontSize="sm" color="gray.200">{t("connect.badgeLawyers")}</Text></HStack>
                        <HStack spacing={2}><Icon as={FaClock} color="#dfba73" /><Text fontSize="sm" color="gray.200">{t("connect.badgeFast")}</Text></HStack>
                        <HStack spacing={2}><Icon as={FaShieldAlt} color="#dfba73" /><Text fontSize="sm" color="gray.200">{t("connect.badgePrivacy")}</Text></HStack>
                    </HStack>

                    <Button
                        size="lg"
                        bg="linear-gradient(135deg, #dfba73 0%, #c5a880 100%)"
                        color="#0b132b"
                        fontWeight="700"
                        borderRadius="xl"
                        px={8}
                        _hover={{ transform: "translateY(-2px)" }}
                        onClick={() => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" })}
                    >
                        {t("connect.btnSendInquiry")}
                    </Button>
                </Container>
            </Box>

            {/* Main Contact Section */}
            <Box py={{ base: 12, md: 20 }} mt={{ base: "-30px", md: "-40px" }} position="relative" zIndex={2}>
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    <Flex direction={{ base: "column", lg: "row" }} gap={{ base: 8, lg: 12 }}>
                        {/* Left Column: Contact Details Cards */}
                        <Box flex="1" w="100%">
                            <Heading as="h2" fontSize={{ base: "xl", md: "2xl" }} fontWeight="800" color="#0b132b" mb={6}>
                                {t("connect.infoTitle")}
                            </Heading>

                            <VStack spacing={5} align="stretch">
                                {/* Address Card with Map Modal */}
                                <Box
                                    bg="white"
                                    p={6}
                                    borderRadius="2xl"
                                    border="1px solid"
                                    borderColor="gray.100"
                                    boxShadow="0 10px 30px -5px rgba(15, 23, 42, 0.05)"
                                    transition="all 0.3s ease"
                                    _hover={{ transform: "translateY(-4px)", borderColor: "brand.300" }}
                                >
                                    <HStack spacing={4} align="flex-start">
                                        <Flex w={12} h={12} borderRadius="xl" bg="brand.50" color="brand.600" align="center" justify="center" flexShrink={0}>
                                            <Icon as={FaMapMarkerAlt} fontSize="20px" />
                                        </Flex>
                                        <Box flex="1">
                                            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
                                                {t("connect.addressTitle")}
                                            </Text>
                                            <Text fontSize="sm" fontWeight="600" color="gray.800" mt={1}>
                                                {t("connect.addressValue")}
                                            </Text>
                                            <Button
                                                variant="link"
                                                color="#dfba73"
                                                fontSize="xs"
                                                fontWeight="700"
                                                mt={2}
                                                onClick={onMapOpen}
                                            >
                                                {t("connect.btnSeeMap")} →
                                            </Button>
                                        </Box>
                                    </HStack>
                                </Box>

                                {/* Phone Card */}
                                <Box
                                    bg="white"
                                    p={6}
                                    borderRadius="2xl"
                                    border="1px solid"
                                    borderColor="gray.100"
                                    boxShadow="0 10px 30px -5px rgba(15, 23, 42, 0.05)"
                                    transition="all 0.3s ease"
                                    _hover={{ transform: "translateY(-4px)", borderColor: "brand.300" }}
                                >
                                    <HStack spacing={4} align="flex-start">
                                        <Flex w={12} h={12} borderRadius="xl" bg="brand.50" color="brand.600" align="center" justify="center" flexShrink={0}>
                                            <Icon as={FaPhoneAlt} fontSize="18px" />
                                        </Flex>
                                        <Box>
                                            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
                                                {t("connect.phonesTitle")}
                                            </Text>
                                            <Heading as="h3" fontSize={{ base: "md", md: "lg" }} fontWeight="800" color="gray.900" mt={1}>
                                                <Box as="a" href="tel:+37477531309" display="block" _hover={{ color: "brand.600" }}>
                                                    +374 77 53 13 09
                                                </Box>
                                                <Box as="a" href="tel:+37455673001" display="block" color="gray.600" fontSize="md" _hover={{ color: "brand.600" }}>
                                                    +374 55 67 30 01
                                                </Box>
                                                <Box as="a" href="tel:+37443134362" display="block" color="gray.600" fontSize="md" _hover={{ color: "brand.600" }}>
                                                    +374 43 13 43 62
                                                </Box>
                                            </Heading>
                                        </Box>
                                    </HStack>
                                </Box>

                                {/* Email Card */}
                                <Box
                                    bg="white"
                                    p={6}
                                    borderRadius="2xl"
                                    border="1px solid"
                                    borderColor="gray.100"
                                    boxShadow="0 10px 30px -5px rgba(15, 23, 42, 0.05)"
                                    transition="all 0.3s ease"
                                    _hover={{ transform: "translateY(-4px)", borderColor: "brand.300" }}
                                >
                                    <HStack spacing={4} align="flex-start">
                                        <Flex w={12} h={12} borderRadius="xl" bg="brand.50" color="brand.600" align="center" justify="center" flexShrink={0}>
                                            <Icon as={FaEnvelope} fontSize="18px" />
                                        </Flex>
                                        <Box>
                                            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
                                                {t("connect.emailTitle")}
                                            </Text>
                                            <Heading as="h3" fontSize={{ base: "md", md: "lg" }} fontWeight="800" color="gray.900" mt={1}>
                                                <Box as="a" href="mailto:info@legaltitan.am" _hover={{ color: "brand.600" }}>
                                                    info@legaltitan.am
                                                </Box>
                                            </Heading>
                                        </Box>
                                    </HStack>
                                </Box>

                                {/* Hours Card */}
                                <Box
                                    bg="white"
                                    p={6}
                                    borderRadius="2xl"
                                    border="1px solid"
                                    borderColor="gray.100"
                                    boxShadow="0 10px 30px -5px rgba(15, 23, 42, 0.05)"
                                    transition="all 0.3s ease"
                                >
                                    <HStack spacing={4} align="flex-start">
                                        <Flex w={12} h={12} borderRadius="xl" bg="brand.50" color="brand.600" align="center" justify="center" flexShrink={0}>
                                            <Icon as={FaClock} fontSize="18px" />
                                        </Flex>
                                        <Box>
                                            <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
                                                {t("connect.daysTitle")}
                                            </Text>
                                            <Text fontSize="sm" fontWeight="700" color="gray.800" mt={1}>
                                                {t("connect.daysValue")}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500" mt={0.5}>
                                                {t("connect.hoursTitle")}: {t("connect.hoursValue")}
                                            </Text>
                                        </Box>
                                    </HStack>
                                </Box>
                            </VStack>
                        </Box>

                        {/* Right Column: Sleek Inquiry Form */}
                        <Box flex="1.2" w="100%" id="contact-form">
                            <Box
                                bg="white"
                                p={{ base: 6, sm: 8, md: 10 }}
                                borderRadius="3xl"
                                border="1px solid"
                                borderColor="gray.100"
                                boxShadow="0 25px 50px -12px rgba(15, 23, 42, 0.08)"
                            >
                                <Box mb={6}>
                                    <Heading as="h2" fontSize={{ base: "22px", md: "28px" }} fontWeight="800" color="gray.900">
                                        {t("connect.formTitle")}
                                    </Heading>
                                </Box>

                                <form onSubmit={handleSubmit}>
                                    <VStack spacing={5}>
                                        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} w="100%">
                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs" fontWeight="700" color="gray.700">
                                                    {t("connect.labelName")}
                                                </FormLabel>
                                                <Input
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder={t("connect.placeholderName")}
                                                    size="lg"
                                                    borderRadius="xl"
                                                    bg="gray.50"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    _focus={{ bg: "white", borderColor: "#dfba73" }}
                                                />
                                            </FormControl>

                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs" fontWeight="700" color="gray.700">
                                                    {t("connect.labelLastname")}
                                                </FormLabel>
                                                <Input
                                                    name="lastname"
                                                    value={formData.lastname}
                                                    onChange={handleChange}
                                                    placeholder={t("connect.placeholderLastname")}
                                                    size="lg"
                                                    borderRadius="xl"
                                                    bg="gray.50"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    _focus={{ bg: "white", borderColor: "#dfba73" }}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="xs" fontWeight="700" color="gray.700">
                                                    {t("connect.labelSurname")}
                                                </FormLabel>
                                                <Input
                                                    name="surname"
                                                    value={formData.surname}
                                                    onChange={handleChange}
                                                    placeholder={t("connect.placeholderSurname")}
                                                    size="lg"
                                                    borderRadius="xl"
                                                    bg="gray.50"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    _focus={{ bg: "white", borderColor: "#dfba73" }}
                                                />
                                            </FormControl>
                                        </SimpleGrid>

                                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} w="100%">
                                            <FormControl isRequired>
                                                <FormLabel fontSize="xs" fontWeight="700" color="gray.700">
                                                    {t("connect.labelEmail")}
                                                </FormLabel>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder={t("connect.placeholderEmail")}
                                                    size="lg"
                                                    borderRadius="xl"
                                                    bg="gray.50"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    _focus={{ bg: "white", borderColor: "#dfba73" }}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="xs" fontWeight="700" color="gray.700">
                                                    {t("connect.labelPhone")}
                                                </FormLabel>
                                                <Input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder={t("connect.placeholderPhone")}
                                                    size="lg"
                                                    borderRadius="xl"
                                                    bg="gray.50"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    _focus={{ bg: "white", borderColor: "#dfba73" }}
                                                />
                                            </FormControl>
                                        </SimpleGrid>

                                        <FormControl isRequired>
                                            <FormLabel fontSize="xs" fontWeight="700" color="gray.700">
                                                {t("connect.labelMessage")}
                                            </FormLabel>
                                            <Textarea
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                placeholder={t("connect.placeholderMessage")}
                                                rows={5}
                                                size="lg"
                                                borderRadius="xl"
                                                bg="gray.50"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                _focus={{ bg: "white", borderColor: "#dfba73" }}
                                            />
                                        </FormControl>

                                        <Button
                                            type="submit"
                                            size="lg"
                                            w="100%"
                                            bg="#0b132b"
                                            color="#dfba73"
                                            borderRadius="xl"
                                            fontWeight="800"
                                            py={7}
                                            boxShadow="0 10px 25px rgba(11, 19, 43, 0.25)"
                                            _hover={{ bg: "#162044", transform: "translateY(-2px)" }}
                                            isLoading={isSubmitting}
                                            loadingText={t("connect.btnSubmitting")}
                                            leftIcon={<FaPaperPlane />}
                                        >
                                            {t("connect.btnSubmit")}
                                        </Button>
                                    </VStack>
                                </form>
                            </Box>
                        </Box>
                    </Flex>

                    <MapModal isOpen={isMapOpen} onClose={onMapClose} />
                </Container>
            </Box>
        </Box>
    );
};

export default ConnectPage;
